import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.css';

/**
 * RichTextEditor - Reusable rich text editor component
 * Supports: Headings (H1-H4), Font family, Font size, Bold, Italic,
 * Underline, Strikethrough, Text/Highlight color, Blockquote, Lists,
 * Links, and Clear formatting. All formats apply to the current text
 * selection, same as any standard rich text editor - select text, then
 * click a toolbar control to restyle just that selection.
 *
 * @param {string} value - Current HTML content
 * @param {function} onChange - Callback when content changes
 * @param {number} maxCharacters - Maximum character limit (default: 500)
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Whether editor is disabled
 */
const RichTextEditor = ({
  value = '',
  showTips = true,
  onChange,
  maxCharacters = 500,
  placeholder = 'Enter your text here...',
  disabled = false,
}) => {
  // Extract plain text character count from HTML
  const getPlainTextLength = (html) => {
    if (!html) return 0;
    // Remove HTML tags and decode entities
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return (temp.innerText || '').length;
  };

  const plainTextLength = getPlainTextLength(value);
  const isNearLimit = plainTextLength > maxCharacters * 0.8;
  const isOverLimit = plainTextLength > maxCharacters;

  // Toolbar configuration. Every format enabled here MUST also be allowed by
  // FormattedContent.js's DOMPurify allowlist, or the formatting will be
  // silently stripped when the note is displayed back - keep the two in sync.
  //
  // Font and size intentionally use Quill's own built-in whitelists (no
  // custom Quill.register call) rather than a wider custom set: they're
  // pre-styled by quill.snow.css (including the dropdown labels), render
  // identically on every platform this app runs on (iOS/Android webapp,
  // desktop), and need nothing beyond mirroring the same handful of CSS
  // classes in FormattedContent.css to look right outside the editor too.
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, false] }],
        [{ font: [] }, { size: [] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ background: [] }],
        ['blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
      ],
    }),
    []
  );

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'background',
    'blockquote',
    'list',
    'link',
  ];

  const handleChange = (html) => {
    // Check character limit (allow override for now, but show warning)
    if (plainTextLength <= maxCharacters) {
      onChange(html);
    }
  };

  return (
    <div className="rich-text-editor-container">
      <ReactQuill
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
        className={`rich-text-editor ${disabled ? 'disabled' : ''}`}
      />

      {/* Character Count */}
      <div
        className={`char-count ${
          isOverLimit ? 'over-limit' : isNearLimit ? 'near-limit' : ''
        }`}
      >
        <span className="count-text">
          {plainTextLength} / {maxCharacters} characters
        </span>
        {isOverLimit && (
          <span className="count-warning">
            ❌ Exceeds character limit
          </span>
        )}
        {isNearLimit && !isOverLimit && (
          <span className="count-warning">
            ⚠️ Approaching limit
          </span>
        )}
      </div>

      {/* Formatting Tips */}
      {showTips && (
      <div className="formatting-tips">
        <span className="tips-label">💡 Tips:</span>
        <span className="tips-text">
          Select text, then use the toolbar to style just that selection -
          heading, font, size, highlight, bold, and more. Press Ctrl+Z to undo.
        </span>
      </div>
      )}
    </div>
  );
};

export default RichTextEditor;
